// src/components/merchant/QuantityTiersEditor.js
// Paliers de prix dégressif — saisie marchand
// Maxula — AbodyStudio Limited

import React from 'react';
import { FiPlus, FiTrash2, FiTrendingDown } from 'react-icons/fi';

const MAX_TIERS = 5;

const fmt = (n) =>
  (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * @param {Array}    value      [{ minQty, discountPercent }]
 * @param {Function} onChange
 * @param {number}   unitPrice  prix unitaire actuel, pour l'aperçu
 */
const QuantityTiersEditor = ({ value = [], onChange, unitPrice = 0 }) => {
  const tiers = Array.isArray(value) ? value : [];

  const update = (i, patch) => {
    const next = tiers.map((t, k) => (k === i ? { ...t, ...patch } : t));
    onChange(next);
  };

  const add = () => {
    if (tiers.length >= MAX_TIERS) return;
    // Suggestion : quantité suivante, remise un cran au-dessus. Un palier
    // pré-rempli de façon cohérente évite la faute la plus fréquente —
    // une remise plus faible sur une quantité plus grande.
    const last = tiers[tiers.length - 1];
    onChange([
      ...tiers,
      {
        minQty: last ? Number(last.minQty) + 1 : 2,
        discountPercent: last ? Math.min(90, Number(last.discountPercent) + 5) : 5,
      },
    ]);
  };

  const remove = (i) => onChange(tiers.filter((_, k) => k !== i));

  // Contrôles de cohérence — affichés, pas bloquants : le marchand peut avoir
  // une raison, mais il doit la voir.
  const warnings = [];
  const sorted = [...tiers].sort((a, b) => Number(a.minQty) - Number(b.minQty));
  for (let i = 1; i < sorted.length; i += 1) {
    if (Number(sorted[i].discountPercent) <= Number(sorted[i - 1].discountPercent)) {
      warnings.push(
        `À partir de ${sorted[i].minQty}, la remise n'est pas meilleure qu'au palier précédent : acheter plus ne rapporte rien au client.`
      );
      break;
    }
  }
  if (tiers.some((t) => Number(t.minQty) < 2)) {
    warnings.push('Un palier commence à 2 minimum — « à partir de 1 » est déjà le prix normal.');
  }

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="p-6 border-b">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <FiTrendingDown /> Prix dégressif (facultatif)
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Faites baisser le prix unitaire quand le client en prend plusieurs.
          Exemple : 2 achetés −5 %, 3 et plus −10 %.
        </p>
      </div>

      <div className="p-6">
        {tiers.length === 0 && (
          <p className="text-sm text-gray-500 mb-4">
            Aucun palier : le prix reste le même quelle que soit la quantité.
          </p>
        )}

        {tiers.map((t, i) => {
          const unit = unitPrice > 0
            ? unitPrice * (1 - (Number(t.discountPercent) || 0) / 100)
            : 0;
          return (
            <div key={i} className="flex flex-wrap items-end gap-3 mb-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">À partir de</label>
                <input
                  type="number"
                  min={2}
                  value={t.minQty}
                  onChange={(e) => update(i, { minQty: parseInt(e.target.value, 10) || 2 })}
                  className="w-24 border border-gray-300 rounded px-3 py-2"
                />
              </div>
              <span className="pb-2 text-sm text-gray-500">achetés</span>

              <div>
                <label className="block text-xs text-gray-600 mb-1">Remise (%)</label>
                <input
                  type="number"
                  min={1}
                  max={90}
                  value={t.discountPercent}
                  onChange={(e) => update(i, { discountPercent: Number(e.target.value) || 0 })}
                  className="w-24 border border-gray-300 rounded px-3 py-2"
                />
              </div>

              {unitPrice > 0 && (
                <div className="pb-2 text-sm">
                  <span className="text-gray-500">soit </span>
                  <span className="font-semibold text-green-700">{fmt(unit)} MAD</span>
                  <span className="text-gray-500"> l'unité</span>
                </div>
              )}

              <button
                type="button"
                onClick={() => remove(i)}
                className="ml-auto p-2 rounded-lg text-red-500 hover:bg-red-50"
                title="Supprimer ce palier"
              >
                <FiTrash2 size={16} />
              </button>
            </div>
          );
        })}

        {warnings.length > 0 && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            {warnings.map((w, i) => <div key={i}>{w}</div>)}
          </div>
        )}

        {tiers.length < MAX_TIERS ? (
          <button
            type="button"
            onClick={add}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-primary-600 text-primary-700 text-sm font-semibold hover:bg-primary-50"
          >
            <FiPlus size={14} /> Ajouter un palier
          </button>
        ) : (
          <p className="text-xs text-gray-500">Maximum {MAX_TIERS} paliers.</p>
        )}
      </div>
    </div>
  );
};

export default QuantityTiersEditor;
